// src/sim/catch/catch.ts — P3 circle-proximity catch detection (GAME-03, D-11).
//
// Invariants:
//   - Pure: returns { updates, events }; never mutates inputs (Pitfall 1).
//   - Deterministic: sorts hunter + hider candidates by id for stable tie-break.
//   - No Phaser physics (D-11 locks this; Arcade overlap nondeterminism rejected
//     in 03-RESEARCH.md §Alternatives Considered).
//   - One catch per hunter per tick — break after first match.
//   - One catch per hider per tick — already-claimed hider skipped by later hunter.
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-11.

import type { Entity, WorldState } from '../../shared/types/world';
import type { GameEvent } from '../../shared/types/events';
import { P1_CHARACTER_RADIUS } from '../movement';
import { P3_CATCH_SLACK_PX } from './constants';

export interface CatchResult {
  /** Partial entity updates keyed by id. Merge into nextEntities in tick(). */
  updates: Record<string, Entity>;
  events: GameEvent[];
}

/**
 * Scan all (hunter × hider) pairs once per tick and emit a `catch` event +
 * linked entity updates whenever a free hunter is within the catch circle of
 * a free, non-hidden, non-immune hider.
 */
export function detectCatches(world: WorldState, currentTick: number): CatchResult {
  const updates: Record<string, Entity> = {};
  const events: GameEvent[] = [];

  const threshold = P1_CHARACTER_RADIUS * 2 + P3_CATCH_SLACK_PX;
  const thresholdSq = threshold * threshold;

  // Sort for deterministic tie-break (id-asc). Stable across replays.
  const hunters = Object.values(world.entities)
    .filter((e) => e.role === 'hunter' && e.status === 'free')
    .sort((a, b) => a.id.localeCompare(b.id));
  const hiders = Object.values(world.entities)
    .filter(
      (e) =>
        e.role === 'hider' &&
        e.status === 'free' &&
        e.hidingSpotId === null &&
        e.floorSpot === null &&
        (e.freedImmunityUntilTick === null || currentTick >= e.freedImmunityUntilTick),
    )
    .sort((a, b) => a.id.localeCompare(b.id));

  for (const hunter of hunters) {
    if (updates[hunter.id]) continue; // already caught one this tick
    for (const hider of hiders) {
      if (updates[hider.id]) continue; // already claimed by another hunter this tick
      const dx = hunter.position.x - hider.position.x;
      const dy = hunter.position.y - hider.position.y;
      if (dx * dx + dy * dy > thresholdSq) continue;
      updates[hunter.id] = {
        ...hunter,
        status: 'escorting',
        escortingEntityId: hider.id,
        recentDropOffTick: null,
        // Clear any leftover foxguard streak — the hunter just changed
        // situation (new catch resets the 4-condition window). D-18 streak
        // null = "no current streak"; the per-tick foxguard evaluator (Plan
        // 07 stage 10) will re-set it next tick if all four conditions hold.
        foxguardConditionsStreakStartTick: null,
      };
      updates[hider.id] = {
        ...hider,
        status: 'caught',
        escortedBy: hunter.id,
      };
      events.push({ type: 'catch', hunterId: hunter.id, hiderId: hider.id, tick: currentTick });
      break; // one catch per hunter per tick
    }
  }
  return { updates, events };
}

/** Squared euclidean distance — copied from src/sim/lighting/lights.ts L80-84. */
// (kept inline above for clarity; export here if a future stage needs it)
